import './kitStyles';
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import type { IconName } from './icons';

/**
 * WEB-3310 юнит 2б-1 — всплывашка кита: одна строка, сама гаснет через 2,5 с
 * (эталон `useToast`, `TOAST_ICON`, `ToastView`,
 * `design/cabinet-rescin/kit.jsx:1122–1137`, кит 02:447–465). Для отказов,
 * которые человек обязан увидеть, — не она, а `Notice`.
 *  - `useToast()` → `[узел, show]`, `show(text, kind)`; вид — всегда явный
 *    параметр: угадывания вида по тексту (`toastKind` эталона) нет — словари двух
 *    языков сделали бы регулярку неверной (решение оркестратора 24.10).
 *  - Четыре вида без рамки, значок цвета смысла: `ok` — `check`, `warn` — `warn`,
 *    `err` — `error` (`role=alert`), `info` — `info`; прочие — `role=status`.
 *  - Верхний слой браузера (`popover="manual"` + `showPopover`), чтобы
 *    уведомление из окна не рисовалось под его затемнением; без `popover` — как
 *    обычный узел.
 *  - Новый показ до истечения перезапускает срок (и с тем же текстом).
 *  - Срок — 2500 мс; место вызова со своим сроком эталона передаёт его
 *    параметром `useToast(ms)` (карта воронки — 2600, editor-c.jsx:728); срок
 *    одного показа — третьим параметром `show(text, kind, ms)` (мост всплывашек
 *    antd, `components/layout/kitMessage.tsx`: у antd срок задаёт вызов, WEB-3767).
 */
export type ToastKind = 'ok' | 'warn' | 'err' | 'info';

const TOAST_ICON: Record<ToastKind, IconName> = { ok: 'check', warn: 'warn', err: 'error', info: 'info' };
const TOAST_MS = 2500;

export interface ToastViewProps {
  text: React.ReactNode;
  kind: ToastKind;
  testid?: string;
  /** Узел в верхний слой — ставит `useToast`; без него `popover` не ставится. */
  popRef?: (el: HTMLDivElement | null) => void;
}

export function ToastView({ text, kind, testid = 'toast', popRef }: ToastViewProps): React.ReactElement {
  // атрибута popover нет в типах React 18 — передаётся как есть
  const top: Record<string, string> = popRef ? { popover: 'manual' } : {};
  return (
    <div
      className="toast"
      role={kind === 'err' ? 'alert' : 'status'}
      data-kind={kind}
      data-testid={testid}
      {...top}
      ref={popRef}
    >
      <Icon name={TOAST_ICON[kind]} size={14} />
      {text}
    </div>
  );
}

function popOpen(el: HTMLElement): boolean {
  try {
    return el.matches(':popover-open');
  } catch {
    return false;
  }
}

export function useToast(
  ms: number = TOAST_MS,
): [React.ReactElement | null, (text: string, kind: ToastKind, ms?: number) => void] {
  const [msg, setMsg] = useState<{ text: string; kind: ToastKind; ms?: number } | null>(null);
  useEffect(() => {
    if (!msg) return undefined;
    const id = setTimeout(() => setMsg(null), msg.ms ?? ms);
    return () => clearTimeout(id);
  }, [msg, ms]);
  const up = useCallback((el: HTMLDivElement | null) => {
    if (el && typeof el.showPopover === 'function' && !popOpen(el)) {
      try {
        el.showPopover();
      } catch {
        /* без popover — обычный узел */
      }
    }
  }, []);
  const show = useCallback(
    (text: string, kind: ToastKind, own?: number) => setMsg({ text, kind, ms: own }),
    [],
  );
  return [msg ? <ToastView text={msg.text} kind={msg.kind} popRef={up} /> : null, show];
}
